import { queryOptions, useQuery } from "@tanstack/react-query";

import type { QueryOptions } from "shared/types";

import { useAuthData } from "shared/providers/auth/context";
import { useAuthFetch } from "shared/providers/auth/use-auth-fetch";

type AccountType = "google" | "icloud";

export type Account = {
  credentials:
    | {
        refresh_token: string;
        app_specific_password?: string;
      }
    | {
        app_specific_password: string;
      };
  date_added: number;
  instructions: string;
  is_outdated?: boolean;
  label: string;
  name: string;
  scopes: string[];
  type: "google" | "icloud";
};

type Response = { connected_accounts: Account[]; id: string };

type Params = {
  name?: string;
  type?: AccountType;
};

export const useConnectedAccountsQueryOptions = <TData = Response>(
  params: Params = {},
  options?: QueryOptions<Response, Error, TData>
) => {
  const { user } = useAuthData();
  const fetch = useAuthFetch();
  return queryOptions({
    queryKey: ["api", "connected-accounts", user.id, params],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const searchParams = new URLSearchParams(params);
      if (!params.name) searchParams.delete("name");
      if (!params.type) searchParams.delete("type");
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/connected-account?${searchParams.toString()}`
      );

      const data: { connected_accounts: Account[]; id: string } =
        await response.json();

      console.log(data);
      return data;
    },
    ...options,
  });
};

export const useConnectedAccountsQuery = <TData = Response>(
  params?: Params,
  options?: QueryOptions<Response, Error, TData>
) => {
  return useQuery(useConnectedAccountsQueryOptions(params, options));
};
